/**
 * Header chip + popup that shows connection + sync state (#1496).
 *
 * Replaces the old "Live / Demo / Login Required" pill that was bound to a
 * single API ping. Now shows one of:
 *   - Live (green)        — online, queue empty, no maintenance
 *   - Syncing N (amber)   — online, N pending sync entries, or actively syncing
 *   - Offline (red)       — navigator.onLine is false
 *   - Maintenance (slate) — recent 'rdm:maintenance' event from api.ts
 *
 * Popup adds: a per-type breakdown of outstanding sync work (#2021 — what
 * hasn't synced, by type), last-sync timestamp, a Sync Now button, and a
 * "Review sync issues" link to /sync-issues only when there are failures.
 *
 * The popup renders through a portal to document.body so it isn't clipped
 * by the PageHeader's overflow-hidden (#1471 follow-up #12).
 */

import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import { useConnectionStatus, ConnectionState } from '../hooks/useConnectionStatus';
import { getLocalDataSummary, type LocalDataItem } from '../utils/localDataSummary';
import { getSyncStatusView, type SyncCategory } from '../utils/syncStatusView';

function localToday(): string {
  const d = new Date();
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

interface StateConfig {
  label: (pending: number) => string;
  dotColor: string;
  textColor: string;
  description: string;
}

const stateConfig: Record<ConnectionState, StateConfig> = {
  online: {
    label: () => 'Live',
    dotColor: 'bg-green-500',
    textColor: 'text-green-700',
    description: 'Connected — your work syncs to the server in real time.',
  },
  syncing: {
    label: (n) => (n > 0 ? `Syncing ${n}` : 'Syncing'),
    dotColor: 'bg-amber-500 animate-pulse',
    textColor: 'text-amber-700',
    description: 'Pending changes are being pushed to the server.',
  },
  offline: {
    label: () => 'Offline',
    dotColor: 'bg-red-500',
    textColor: 'text-red-700',
    description: 'No network. Your work is saved on this device and will sync when you reconnect.',
  },
  maintenance: {
    label: () => 'Maintenance',
    dotColor: 'bg-slate-400',
    textColor: 'text-slate-700',
    description: 'The server is briefly unavailable (deploy or maintenance). Your work is safe locally.',
  },
};

function formatTime(iso: string | null): string {
  if (!iso) return 'never';
  try {
    return new Date(iso).toLocaleTimeString();
  } catch {
    return iso;
  }
}

const POPUP_WIDTH = 288; // Tailwind w-72 = 18rem = 288px
const VIEWPORT_MARGIN = 8;

export default function ApiStatusIndicator() {
  const conn = useConnectionStatus();
  const [showDetails, setShowDetails] = useState(false);
  const [localData, setLocalData] = useState<LocalDataItem[]>([]);
  const [syncView, setSyncView] = useState<SyncCategory[]>([]);
  const cfg = stateConfig[conn.state];

  // Load the synced/total view + "on this device" counts only while the popup
  // is open (and refresh when sync state changes). Re-runs on pendingCount so a
  // just-synced item updates the X/Y promptly.
  useEffect(() => {
    if (!showDetails) return;
    let cancelled = false;
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/New_York';
    getSyncStatusView(localToday(), tz)
      .then((v) => {
        if (!cancelled) setSyncView(v);
      })
      .catch(() => undefined);
    getLocalDataSummary()
      .then((d) => {
        if (!cancelled) setLocalData(d);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [showDetails, conn.pendingCount, conn.failedCount]);

  const triggerRef = useRef<HTMLButtonElement>(null);
  const [popupPos, setPopupPos] = useState<{ top: number; left: number } | null>(null);

  // Compute absolute document-space coords whenever the popup opens or the
  // viewport changes. Using page-absolute coords (scrollX/Y + rect) so the
  // popup stays anchored even if the page scrolls while it's open.
  useLayoutEffect(() => {
    if (!showDetails) {
      setPopupPos(null);
      return;
    }
    const updatePos = () => {
      const el = triggerRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const top = rect.bottom + window.scrollY + 8; // 8px gap
      // Right-align the popup with the chip, clamping inside the viewport.
      const desiredLeft = rect.right + window.scrollX - POPUP_WIDTH;
      const maxLeft = window.scrollX + window.innerWidth - POPUP_WIDTH - VIEWPORT_MARGIN;
      const minLeft = window.scrollX + VIEWPORT_MARGIN;
      const left = Math.max(minLeft, Math.min(desiredLeft, maxLeft));
      setPopupPos({ top, left });
    };
    updatePos();
    window.addEventListener('resize', updatePos);
    window.addEventListener('scroll', updatePos, true);
    return () => {
      window.removeEventListener('resize', updatePos);
      window.removeEventListener('scroll', updatePos, true);
    };
  }, [showDetails]);

  return (
    <>
      <button
        ref={triggerRef}
        onClick={() => setShowDetails((v) => !v)}
        className="flex items-center gap-1.5 rounded-full bg-white/10 px-2 py-1 text-xs font-medium text-white/90 transition-colors hover:bg-white/20"
        title={cfg.description}
      >
        <span className={`h-2 w-2 rounded-full ${cfg.dotColor}`} />
        <span>{cfg.label(conn.pendingCount)}</span>
      </button>

      {showDetails && popupPos && createPortal(
        <>
          <div className="fixed inset-0 z-[60]" onClick={() => setShowDetails(false)} />
          <div
            className="absolute z-[70] w-72 rounded-xl bg-white p-4 shadow-lg"
            style={{ top: popupPos.top, left: popupPos.left }}
          >
            <div className="flex items-start gap-3">
              <div className={`mt-0.5 h-3 w-3 rounded-full ${cfg.dotColor.replace(' animate-pulse', '')}`} />
              <div className="flex-1">
                <h4 className={`font-semibold ${cfg.textColor}`}>{cfg.label(conn.pendingCount)}</h4>
                <p className="mt-0.5 text-sm text-slate-600">{cfg.description}</p>
              </div>
            </div>

            {/* Synced today (#1950 Phase 2) — synced/total per type, derived from
                the rep-activity event stream (server = authoritative denominator,
                so it's device-independent). Degrades to "N pending" offline. */}
            <div className="mt-3 border-t border-slate-100 pt-3">
              {syncView.length === 0 ? (
                <div className="flex items-center gap-2 text-sm font-medium text-green-700">
                  <span aria-hidden>✓</span>
                  <span>Everything is synced</span>
                </div>
              ) : (
                <>
                  <div className="mb-1.5 text-[11px] font-medium uppercase tracking-wide text-slate-400">
                    Synced today
                  </div>
                  <ul className="space-y-1.5">
                    {syncView.map((c) => {
                      const complete = c.synced !== null && c.pending === 0 && c.failed === 0;
                      const tone = c.failed > 0
                        ? 'text-red-700'
                        : complete
                          ? 'text-green-700'
                          : c.synced === null
                            ? 'text-slate-500'
                            : 'text-amber-700';
                      return (
                        <li key={c.key} className="flex items-center justify-between gap-2 text-sm">
                          <span className="text-slate-600">{c.label}</span>
                          <span className={`font-medium ${tone}`}>
                            {c.synced === null ? `${c.pending + c.failed} pending` : `${c.synced}/${c.total}`}
                            {c.failed > 0 && <span className="text-red-700"> · {c.failed} failed</span>}
                          </span>
                        </li>
                      );
                    })}
                  </ul>
                </>
              )}
              <div className="mt-2 text-xs text-slate-400">Last sync · {formatTime(conn.lastSyncAt)}</div>
            </div>

            <div className="mt-3 flex gap-2">
              <button
                disabled={conn.isSyncing || !conn.isOnline}
                onClick={() => {
                  conn.syncNow();
                }}
                className="flex-1 rounded-lg bg-primary-600 py-2 text-sm font-medium text-white transition-colors hover:bg-primary-700 disabled:bg-slate-200 disabled:text-slate-400"
              >
                {conn.isSyncing ? 'Syncing…' : 'Sync Now'}
              </button>
            </div>

            {/* "On this device" (#2021) — DOMAIN data cached locally, distinct
                from the sync outbox above. Reassures that local state is real
                and re-synced from the server on each device. */}
            <div className="mt-3 border-t border-slate-100 pt-3">
              <div className="mb-1.5 text-[11px] font-medium uppercase tracking-wide text-slate-400">
                On this device
              </div>
              <ul className="space-y-1">
                {localData.map((d) => (
                  <li key={d.key} className="flex items-center justify-between text-sm">
                    <span className="text-slate-600">{d.label}</span>
                    <span className="font-medium text-slate-700">{d.count}</span>
                  </li>
                ))}
              </ul>
              <p className="mt-1.5 text-[10px] text-slate-400">
                Saved locally · re-synced from the server on each device.
              </p>
            </div>

            {conn.failedCount > 0 && (
              <Link
                to="/sync-issues"
                onClick={() => setShowDetails(false)}
                className="mt-3 block w-full rounded-lg bg-red-50 py-2 text-center text-xs font-medium text-red-700 transition-colors hover:bg-red-100"
              >
                Review {conn.failedCount} sync {conn.failedCount === 1 ? 'issue' : 'issues'} →
              </Link>
            )}
          </div>
        </>,
        document.body,
      )}
    </>
  );
}
